import { useCallback, useMemo, useState } from 'react';
import { useDynamicCallback } from '../../hooks';
import { Button, ButtonVariants, IconButton } from '../Button';
import { Box, Flex } from '../Core';
import { DrawerContent, DrawerFooter, type DrawerProps } from '../Drawer';
import { FormControlSizes } from '../Form';
import { IconName } from '../Icons';
import { IndicatorBadge } from '../IndicatorBadge';
import { IndicatorDotVariants } from '../IndicatorDot';
import { RR6Prompt } from '../Prompt';
import { Tab, TabList, Tabs, TabSize } from '../Tabs';
import { Text } from '../Text';
import { DrawerHeader } from './styles';
import type { EditingDrawerTab } from './types';

/**
 * Legacy multi-tab edit drawer used by Market Accounts.
 * Do not iterate further on save orchestration / tab contracts here — next step is
 * migrating callers to EntityAdmin, which owns multi-section save semantics properly.
 */
type EntityEditingDrawerContentProps = {
  drawerTitle: string;
  tabs: EditingDrawerTab[];
} & DrawerProps;

export function EntityEditingDrawerContent({ drawerTitle, tabs, close, isOpen }: EntityEditingDrawerContentProps) {
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const handleTabChange = useCallback((tabIndex: number) => {
    setActiveTabIndex(tabIndex);
  }, []);

  const viewableTabs = useMemo(() => tabs.filter(t => t.viewable), [tabs]);

  const handleSaveChanges = useCallback(() => {
    // Only persist dirty tabs, and run them sequentially. Parallel saves for wallet
    // accounts raced: source-data PATCH reloaded a stale row and overwrote Group.
    // A rejected tab save must abort the rest to avoid partial writes across tabs.
    void (async () => {
      try {
        for (const [tabIndex, tab] of viewableTabs.entries()) {
          if (!tab.isDirty) {
            continue;
          }
          await tab.save().catch(error => {
            setActiveTabIndex(tabIndex);
            throw error;
          });
        }
      } catch {
        // Tab save handlers toast failures; swallow here so remaining dirty tabs do not run.
      }
    })();
  }, [viewableTabs]);

  const handleMouseOverSaveButton = useCallback(() => {
    viewableTabs.forEach(tab => tab.touchAll());
  }, [viewableTabs]);

  const someTabHasError = viewableTabs.some(tab => tab.hasError);
  const isDirty = viewableTabs.some(tab => tab.isDirty);
  const isDirtyMap = new Map(viewableTabs.map(tab => [tab.name, tab.isDirty]));

  const handleClose = useDynamicCallback(() => {
    if (!isDirty) {
      return close();
    }

    const confirmLeave = window.confirm('You have unsaved changes. Are you sure you want to leave?');
    if (confirmLeave) {
      close();
    }
  });

  return (
    <>
      <RR6Prompt when={isOpen && isDirty} message="You have unsaved changes. Are you sure you want to leave?" />
      <DrawerHeader>
        <Flex gap="spacing4">
          <Text color="colorTextImportant">{drawerTitle}</Text>
        </Flex>
        <IconButton size={FormControlSizes.Small} icon={IconName.Close} onClick={handleClose} />
      </DrawerHeader>
      <Tabs
        data-testid="edit-entity-drawer-tabs"
        w="100%"
        h="100%"
        selectedIndex={activeTabIndex}
        onSelect={handleTabChange}
        size={TabSize.Large}
      >
        <TabList isBordered flex="0 0 auto">
          {viewableTabs.map(tab => (
            <Tab
              data-testid={`edit-entity-drawer-tab-${tab.name}`}
              showDot={isDirtyMap.get(tab.name)}
              dotVariant={IndicatorDotVariants.Primary}
              label={tab.name}
              suffix={tab.badgeContent ? <IndicatorBadge children={tab.badgeContent} /> : null}
              key={tab.name}
            />
          ))}
        </TabList>
        <DrawerContent>
          <Flex h="100%" flexDirection="column">
            {viewableTabs.map(tab => (
              <Box
                key={tab.name}
                display={viewableTabs[activeTabIndex].name === tab.name ? 'flex' : 'none'}
                w="100%"
                h="100%"
              >
                {tab.component}
              </Box>
            ))}
          </Flex>
        </DrawerContent>
      </Tabs>
      <DrawerFooter>
        <Button onClick={handleClose} data-testid="edit-entity-drawer-close-button">
          Close
        </Button>
        <Button
          data-testid="edit-entity-drawer-save-button"
          onClick={handleSaveChanges}
          onMouseOver={handleMouseOverSaveButton}
          disabled={!isDirty || someTabHasError}
          variant={ButtonVariants.Primary}
        >
          Save
        </Button>
      </DrawerFooter>
    </>
  );
}
